import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import config from '../config/config';
import { ArrowLeft, MessageCircle, User, Clock, Eye } from 'lucide-react';

const AdminUserConversations = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Verificar que el usuario sea admin
  useEffect(() => {
    if (!config.isAdmin(currentUser)) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Cargar conversaciones del usuario
  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, pagination.page]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAdminUserConversations(
        userId,
        pagination.page,
        pagination.limit
      );

      setUserData({
        id: response.userId,
        username: response.username
      });
      setConversations(response.conversations || []);
      setPagination({
        ...pagination,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !userData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando conversaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-primary transition-all duration-200 hover:scale-105 active:scale-95 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver</span>
          </button>

          <div className="flex items-center space-x-3">
            <MessageCircle className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Conversaciones de @{userData?.username}
              </h1>
              <p className="text-gray-600 mt-1">
                Todas las conversaciones del usuario
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-gray-700">
                Total de conversaciones:
              </span>
              <span className="text-sm font-bold text-primary">
                {pagination.total}
              </span>
            </div>
            <Link
              to={`/perfil/${userId}`}
              className="text-sm text-primary hover:underline"
            >
              Ver perfil del usuario
            </Link>
          </div>
        </div>

        {/* Lista de conversaciones */}
        {conversations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Este usuario no tiene conversaciones</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conversations.map((conversation) => (
              <div
                key={conversation.conversationId}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between">
                  {/* Información del otro usuario */}
                  <div className="flex items-center space-x-4 flex-1">
                    <img
                      src={
                        conversation.otherUser.profilePicture ||
                        '/images/default-avatar.svg'
                      }
                      alt={conversation.otherUser.username}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {conversation.otherUser.firstName ||
                            conversation.otherUser.username}
                        </h3>
                        <span className="text-sm text-gray-500">
                          @{conversation.otherUser.username}
                        </span>
                      </div>
                      {conversation.otherUser.email && (
                        <p className="text-xs text-gray-500 mt-1">
                          {conversation.otherUser.email}
                        </p>
                      )}

                      {/* Último mensaje */}
                      {conversation.lastMessage && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {conversation.lastMessage.message}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>
                                {formatDate(conversation.lastMessage.created_at)}
                              </span>
                            </span>
                            <span>
                              {conversation.messageCount} mensajes
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Botón ver mensajes */}
                  <Link
                    to={`/admin/mensajes/${userId}/${conversation.otherUser.id}`}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap ml-4"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Ver mensajes</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex justify-center space-x-2">
            <button
              onClick={() =>
                setPagination({ ...pagination, page: pagination.page - 1 })
              }
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            <span className="px-4 py-2 text-gray-700">
              Página {pagination.page} de {pagination.pages}
            </span>
            <button
              onClick={() =>
                setPagination({ ...pagination, page: pagination.page + 1 })
              }
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserConversations;

